/**
 * User Settings Service
 *
 * Manages user preferences that sync across devices.
 * Settings are stored in the profiles.settings JSONB column.
 *
 * ISSUE_167: Dexie 큐 제거 — 순수 Supabase(서버 권위).
 * 낙관 갱신은 소비 훅(useSettingsSync → useGlobalStore)이 담당.
 *
 * @module userSettingsService
 */

import { supabase, isSupabaseConfigured } from "~/shared/lib/supabase";
import { queryWithAuthRetry, mutateWithAuthRetry } from "~/core/api/executeWithAuthRetry";

// ============================================================================
// TYPES
// ============================================================================

export interface UserSettings {
  theme?: string;           // 'soft' | 'brutal' | 'neon' | etc.
  isDarkMode?: boolean;
  designStyle?: string;     // 'soft' | 'brutal'
  activeCustomThemeId?: string | null;
  pinnedModules?: string[]; // AppModule[] — 서비스 레이어는 스토어 타입에 의존하지 않음
  flowGuideSimplified?: boolean;  // 흐름 생성 안내 간소화
}

// Default settings when none exist
export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'soft',
  isDarkMode: false,
  designStyle: 'soft',
  activeCustomThemeId: null,
  // pinnedModules 의도적 생략: undefined = 로컬 값 유지 (덮어쓰지 않음)
};

// ============================================================================
// FETCH SETTINGS
// ============================================================================

/**
 * Fetch user settings from profiles table
 */
export async function fetchUserSettings(userId: string): Promise<UserSettings | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    // Note: 'settings' column may not be in generated types yet - using type assertion
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!.from('profiles').select('settings').eq('id', userId).maybeSingle()
    ) as { data: { settings?: UserSettings } | null; error: { message: string } | null };

    if (error) {
      console.error('[userSettingsService] Error fetching settings:', error);
      return null;
    }

    // Merge with defaults to ensure all fields exist
    const settings = data?.settings || {};
    return { ...DEFAULT_SETTINGS, ...settings };
  } catch (err) {
    console.error('[userSettingsService] Unexpected error:', err);
    return null;
  }
}

// ============================================================================
// UPDATE SETTINGS — ISSUE_167: 순수 Supabase
// ============================================================================

/**
 * Update user settings — Supabase update(서버 권위).
 * Full replace - we always send complete settings object
 */
export async function updateUserSettings(
  userId: string,
  settings: UserSettings
): Promise<boolean> {
  if (!isSupabaseConfigured() || !supabase) {
    return false;
  }

  const { error } = await mutateWithAuthRetry(() =>
    supabase!.from('profiles').update({ settings } as Record<string, unknown>).eq('id', userId)
  );

  if (error) {
    console.error('[userSettingsService] Error updating settings:', error);
    return false;
  }

  return true;
}
